import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useCart } from '../contexts/CartContext';
import SEO from '../components/SEO';

const CartPage: React.FC = () => {
  const { cartItems, removeFromCart, updateQuantity, clearCart } = useCart();
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  // Generate partner checkout URL with cart items
  const generateCheckoutUrl = () => {
    if (cartItems.length === 0) return '';

    const baseUrl = 'https://www.amazon.com/gp/aws/cart/add.html';
    
    // Get Associate Tag from .env if available
    const associateTag = process.env.REACT_APP_AMAZON_ASSOCIATE_TAG || 'goodcart-20';
    
    // Build URL parameters
    const params = new URLSearchParams();
    if (associateTag) {
      params.append('AssociateTag', associateTag);
    }
    
    // Add each item to the URL parameters
    cartItems.forEach((item, index) => {
      params.append(`ASIN.${index + 1}`, item.asin);
      params.append(`Quantity.${index + 1}`, item.quantity.toString());
    });
    
    return `${baseUrl}?${params.toString()}`;
  };

  // Handle checkout
  const handleCheckout = () => {
    setIsCheckingOut(true);
    // Redirect to checkout after a short delay to show loading state
    setTimeout(() => {
      window.open(generateCheckoutUrl(), '_blank');
      setIsCheckingOut(false);
    }, 500);
  };

  if (cartItems.length === 0) {
    return (
      <div className="bg-gray-50 py-12">
        <div className="container-custom">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Your Cart</h1>
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-16 w-16 mx-auto text-gray-400 mb-4" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" 
              />
            </svg>
            <h2 className="text-xl font-semibold text-gray-700 mb-4">Your cart is empty</h2>
            <p className="text-gray-600 mb-6">Looks like you haven't added any products to your cart yet.</p>
            <Link to="/search" className="btn btn-primary">
              Browse Products
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 py-12">
      <SEO
        title="Your Shopping Cart"
        description="Review your cart and check out securely with GoodCart."
        url="https://goodcart.org/cart"
      />
      
      <div className="container-custom">
        <h1 className="text-3xl font-display font-bold text-gray-900 mb-8">Your Cart</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-display font-semibold text-gray-800">
                    {cartItems.length} {cartItems.length === 1 ? 'Item' : 'Items'}
                  </h2>
                  <button 
                    onClick={clearCart}
                    className="text-sm text-red-600 hover:text-red-800 font-medium transition-colors duration-200"
                  >
                    Clear Cart
                  </button>
                </div>
              </div>
              
              <ul className="divide-y divide-gray-200">
                {cartItems.map((item) => (
                  <motion.li 
                    key={item.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="p-6"
                  >
                    <div className="flex flex-col sm:flex-row">
                      <div className="sm:w-24 sm:h-24 h-20 w-20 flex-shrink-0 overflow-hidden rounded-md border border-gray-200 mb-4 sm:mb-0">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="h-full w-full object-cover object-center"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = `https://placehold.co/200x200@3x?text=${item.name.replace(/ /g, '+')}&font=poppins`;
                          }}
                        />
                      </div>
                      
                      <div className="sm:ml-6 flex-1">
                        <div className="flex justify-between">
                          <div>
                            <h3 className="text-lg font-display font-medium text-gray-900 mb-2">
                              <Link to={`/products/${item.id}`} className="hover:text-brand-green-600">
                                {item.name}
                              </Link>
                            </h3>
                            
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFromCart(item.id)}
                            className="text-gray-400 hover:text-red-500 transition-colors duration-200"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </div>
                        
                        <div className="flex items-center mt-2">
                          <label htmlFor={`quantity-${item.id}`} className="text-sm font-medium text-gray-700 mr-3">
                            Quantity:
                          </label>
                          <div className="flex items-center border border-gray-300 rounded-md">
                            <button
                              type="button"
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                              className="p-2 text-gray-600 hover:text-gray-800"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                              </svg>
                            </button>
                            <input
                              id={`quantity-${item.id}`}
                              name={`quantity-${item.id}`}
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => updateQuantity(item.id, parseInt(e.target.value) || 1)}
                              className="w-12 text-center border-0 focus:ring-0"
                            />
                            <button
                              type="button"
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              className="p-2 text-gray-600 hover:text-gray-800"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>
          
          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-6">
              <h2 className="text-lg font-display font-semibold text-gray-900 mb-6">Order Summary</h2>
              
              <div className="mb-6">
                <div className="flex justify-between text-sm mb-2">
                  <p className="text-gray-600">Items ({cartItems.reduce((total, item) => total + item.quantity, 0)})</p>
                  <p className="font-medium text-gray-900">Calculated at checkout</p>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <p className="text-gray-600">Shipping</p>
                  <p className="font-medium text-gray-900">Calculated at checkout</p>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <p className="text-gray-600">Tax</p>
                  <p className="font-medium text-gray-900">Calculated at checkout</p>
                </div>
              </div>
              
              <div className="border-t border-gray-200 pt-4 mb-6">
                <div className="flex justify-between text-base font-medium text-gray-900">
                  <p>Total</p>
                  <p>Calculated at checkout</p>
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Prices and shipping costs are calculated at checkout.
                </p>
              </div>
              
              <button
                type="button"
                onClick={handleCheckout}
                disabled={isCheckingOut}
                className={`w-full btn btn-primary flex items-center justify-center font-medium shadow-md hover:shadow-lg transition-shadow ${isCheckingOut ? 'opacity-75 cursor-not-allowed' : ''}`}
              >
                {isCheckingOut ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                    </svg>
                    Proceed to checkout
                  </>
                )}
              </button>
              
              <div className="mt-6 text-center">
                <Link to="/search" className="text-sm text-brand-green-600 hover:text-brand-green-800 font-medium">
                  Continue Shopping
                </Link>
              </div>
              
              {/* Trust Badges */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="text-sm font-medium text-gray-700 mb-4">Secure Checkout</h3>
                <div className="flex flex-wrap justify-center gap-3">
                  <div className="bg-gray-100 rounded-md p-2 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    <span className="text-xs">Secure</span>
                  </div>
                  <div className="bg-gray-100 rounded-md p-2 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    <span className="text-xs">Trusted</span>
                  </div>
                  <div className="bg-gray-100 rounded-md p-2 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                    <span className="text-xs">Secure payment</span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 text-center mt-4">
                  Your information is securely processed by our checkout partner.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;